import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";
import { useCountUp, useScrollAnimation } from "@/hooks/useScrollAnimation";

const tiers = [
  { followers: "500", subtext: "Starter audience", amount: 235, note: "That's more than TikTok pays for 10 million views." },
  { followers: "5,000", subtext: "Growing creator", amount: 2350, note: "More than most full-time salaries." },
  { followers: "50,000", subtext: "Established creator", amount: 23500, note: "This is why your audience is already worth more than you think." },
];

const IncomeCard = ({ tier, delay }: { tier: typeof tiers[0]; delay: number }) => {
  const { ref, isVisible } = useScrollAnimation(0.2);
  const count = useCountUp(tier.amount, 1500, isVisible);

  return (
    <ScrollReveal delay={delay}>
      <div
        ref={ref}
        className="rounded-2xl p-5 md:p-8 flex flex-col items-center text-center bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        style={{ border: "1px solid #E5E7EB" }}
      >
        <h3 className="font-display text-xl md:text-3xl font-bold mb-1" style={{ color: "#111827" }}>
          {tier.followers} Followers
        </h3>
        <p className="text-sm mb-4" style={{ color: "#9CA3AF" }}>{tier.subtext}</p>
        <p className="font-display font-bold text-2xl md:text-4xl mb-4 flex items-center gap-2" style={{ color: "#F59E0B" }}>
          <TrendingUp className="w-5 h-5 md:w-6 md:h-6" />
          ${count.toLocaleString()}/month
        </p>
        <p className="text-xs md:text-sm leading-relaxed" style={{ color: "#6B7280" }}>
          {tier.note}
        </p>
      </div>
    </ScrollReveal>
  );
};

export const AnySizeWorksSection = () => {
  return (
    <section className="py-10 md:py-20 relative" style={{ background: "#FAFAFA" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-14">
            <h2 className="font-display font-bold mb-3 md:mb-6 leading-tight" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 40px)" }}>
              Any Audience Size. <span style={{ color: "#F59E0B" }}>Real Income.</span>
            </h2>
            <p className="text-sm md:text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
              1% of your audience buying a $47 ebook. Here's what that looks like.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 max-w-5xl mx-auto mb-10 md:mb-14">
          {tiers.map((tier, i) => (
            <IncomeCard key={tier.followers} tier={tier} delay={i * 100} />
          ))}
        </div>

        <div className="flex justify-center">
          <a href="#pricing" className="inline-block w-full md:w-auto">
            <Button variant="hero" size="xl" className="group text-base md:text-lg px-8 py-5 md:px-12 md:py-6 w-full md:w-auto max-w-[400px] btn-interactive">
              Start Your Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};
